const COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const MAX_TEXT_LENGTH = 4000;

// The hard per-stroke ceiling of the governed snapshot, EXPORTED because it is a
// real limit a capture must satisfy: past it normalizedPoints() below returns
// null and the snapshot silently loses its geometry. The published pen-point
// budget (env.FEEDBACK.maxPenPointsPerStroke) is derived from this number, so a
// client can never be told it may draw a stroke that cannot be stored.
export const ANNOTATION_MAX_POINTS = 2048;

const MAX_POINTS = ANNOTATION_MAX_POINTS;
const MAX_SERIALIZED_BYTES = 64 * 1024;

function finite(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function color(value) {
  return COLOR_PATTERN.test(String(value || '')) ? String(value) : null;
}

function annotationType(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return (
    {
      rectangle: 'box',
      box: 'box',
      arrow: 'arrow',
      highlight: 'highlight',
      pin: 'pin',
      text: 'text',
      pen: 'pen',
      freehand: 'pen',
    }[normalized] || null
  );
}

function normalizedBounds(bounds, imageWidth, imageHeight) {
  const x = clamp(finite(bounds?.x), 0, imageWidth);
  const y = clamp(finite(bounds?.y), 0, imageHeight);
  const width = clamp(finite(bounds?.width), 0, imageWidth - x);
  const height = clamp(finite(bounds?.height), 0, imageHeight - y);
  return { x, y, width, height };
}

function normalizedPoints(points, imageWidth, imageHeight) {
  if (!Array.isArray(points) || points.length > MAX_POINTS) return null;
  const normalized = points
    .map((point) => {
      const x = Array.isArray(point) ? point[0] : point?.x;
      const y = Array.isArray(point) ? point[1] : point?.y;
      if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) return null;
      return [clamp(Number(x), 0, imageWidth), clamp(Number(y), 0, imageHeight)];
    })
    .filter(Boolean);
  return normalized.length ? normalized : null;
}

function denormalizedLocalPoints(localPoints, bounds, imageWidth, imageHeight) {
  if (!Array.isArray(localPoints)) return null;
  return normalizedPoints(
    localPoints.map((point) => ({
      x: bounds.x + finite(Array.isArray(point) ? point[0] : point?.x) * bounds.width,
      y: bounds.y + finite(Array.isArray(point) ? point[1] : point?.y) * bounds.height,
    })),
    imageWidth,
    imageHeight,
  );
}

export function normalizeAnnotationSnapshot(annotation, image, { annotationId, markNumber } = {}) {
  const type = annotationType(annotation?.type);
  const imageWidth = Math.max(1, finite(image?.width || image?.naturalWidth, 1));
  const imageHeight = Math.max(1, finite(image?.height || image?.naturalHeight, 1));
  if (!type) return null;
  const style = annotation?.style || {};
  const bounds = normalizedBounds(annotation?.bounds, imageWidth, imageHeight);
  const localPoints = denormalizedLocalPoints(
    annotation?.localPoints,
    bounds,
    imageWidth,
    imageHeight,
  );
  const arrowPoints = type === 'arrow' ? localPoints : null;
  const snapshot = {
    schemaVersion: 1,
    annotationId: String(annotationId || annotation?.id || '').trim() || null,
    markNumber: Math.max(1, Math.trunc(finite(markNumber, 1))),
    type,
    imageWidth,
    imageHeight,
    coordinateSpace: 'capture-pixels',
    bounds,
    rotationDegrees: clamp(
      finite(annotation?.rotationDeg ?? annotation?.rotationDegrees ?? annotation?.rotation),
      -360,
      360,
    ),
    strokeColor: color(annotation?.strokeColor || style.strokeColor),
    fillColor: color(annotation?.fillColor || style.fillColor),
    textColor: color(annotation?.textColor || style.textColor),
    backgroundMode: annotation?.textBackgroundMode || style.textBackgroundMode || 'transparent',
    text: annotation?.text ?? annotation?.instruction ?? null,
    arrow:
      arrowPoints?.length >= 2
        ? {
            start: { x: arrowPoints[0][0], y: arrowPoints[0][1] },
            end: { x: arrowPoints[1][0], y: arrowPoints[1][1] },
          }
        : annotation?.arrow || annotation?.start || annotation?.end
          ? {
              start: annotation?.arrow?.start || annotation?.start || null,
              end: annotation?.arrow?.end || annotation?.end || null,
            }
          : null,
    freehandPoints:
      type === 'pen'
        ? localPoints || normalizedPoints(annotation?.points, imageWidth, imageHeight)
        : null,
  };
  if (snapshot.text != null) snapshot.text = String(snapshot.text).slice(0, MAX_TEXT_LENGTH);
  const serializedBytes = new TextEncoder().encode(JSON.stringify(snapshot)).length;
  return serializedBytes <= MAX_SERIALIZED_BYTES ? snapshot : null;
}

export function escapeSvgText(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function isAnnotationSnapshot(snapshot) {
  const baseValid = Boolean(
    snapshot &&
    snapshot.schemaVersion === 1 &&
    annotationType(snapshot.type) &&
    Number(snapshot.imageWidth) > 0 &&
    Number(snapshot.imageHeight) > 0 &&
    snapshot.bounds &&
    Number.isFinite(Number(snapshot.bounds.x)) &&
    Number.isFinite(Number(snapshot.bounds.y)) &&
    Number(snapshot.bounds.width) >= 0 &&
    Number(snapshot.bounds.height) >= 0 &&
    snapshot.coordinateSpace === 'capture-pixels',
  );
  if (!baseValid) return false;
  const type = annotationType(snapshot.type);
  if (type === 'arrow') {
    return ['start', 'end'].every(
      (key) =>
        Number.isFinite(Number(snapshot.arrow?.[key]?.x)) &&
        Number.isFinite(Number(snapshot.arrow?.[key]?.y)),
    );
  }
  if (type === 'pen') {
    return Array.isArray(snapshot.freehandPoints) && snapshot.freehandPoints.length >= 2;
  }
  return snapshot.bounds.width > 0 && snapshot.bounds.height > 0;
}
